import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import supabase, { checkSupabaseConnection, withTimeout } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supabaseConnected, setSupabaseConnected] = useState(false);

  const fetchUserProfile = async (authUser) => {
    try {
      // Added a strict 5s timeout. If the DB hangs, we don't want the UI to freeze forever
      const { data: profile, error } = await withTimeout(
        supabase.from('profiles').select('*').eq('id', authUser.id).single(),
        5000
      );
      
      if (error) throw error;
      
      setUser({
        id: authUser.id,
        email: authUser.email,
        name: profile?.name || authUser.email.split('@')[0],
        isAdmin: profile?.is_admin || false,
        createdAt: authUser.created_at
      });
    } catch (error) {
      console.warn('Profile fetch failed, using auth data fallback:', error.message);
      // Fallback guarantees the user can still log in even if the profiles table is unreachable
      setUser({
        id: authUser.id,
        email: authUser.email,
        name: authUser.email.split('@')[0],
        isAdmin: false,
        createdAt: authUser.created_at
      });
    }
  };

  const verifyConnection = useCallback(async () => {
    const connStatus = await checkSupabaseConnection();
    setSupabaseConnected(connStatus.connected);
    return connStatus.connected;
  }, []);

  useEffect(() => {
    let isMounted = true;

    // 0. Ultimate Safety Net: Force unlock the UI after 8 seconds no matter what
    const safetyTimer = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
        console.warn("Auth initialization timed out. Forcefully unlocking UI.");
      }
    }, 8000);

    // 1. Handle tokens immediately before anything else using robust Regex
    const handleInitialSession = async () => {
      const hash = window.location.hash || window.location.search;
      const tokenMatch = hash.match(/access_token=([^&]+)/);
      const typeMatch = hash.match(/type=([^&]+)/);
      const refreshMatch = hash.match(/refresh_token=([^&]+)/);

      if (tokenMatch && typeMatch && typeMatch[1] === 'recovery') {
        const accessToken = tokenMatch[1];
        const refreshToken = refreshMatch ? refreshMatch[1] : null;

        if (accessToken && refreshToken) {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          if (!error && data.session) {
            window.location.hash = '#/update-password';
          }
        }
      }
    };

    const initAuth = async () => {
      if (isMounted) setLoading(true);
      
      try {
        const isConnected = await verifyConnection();
        
        if (isConnected) {
          await handleInitialSession();
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            await fetchUserProfile(session.user);
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
          clearTimeout(safetyTimer); // Clear the safety timer if we finished properly
        }
      }
    };

    // 2. Listen for Supabase events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          if (isMounted) setLoading(false);
          window.location.hash = '#/update-password';
        } else if (event === 'SIGNED_IN' && session) {
          await fetchUserProfile(session.user);
          if (isMounted) {
            setSupabaseConnected(true);
            setLoading(false);
          }
        } else if (event === 'SIGNED_OUT') {
          if (isMounted) {
            setUser(null);
            setLoading(false);
          }
          sessionStorage.removeItem('adminUnlocked');
        }
      }
    );

    initAuth();

    return () => {
      isMounted = false;
      clearTimeout(safetyTimer);
      subscription?.unsubscribe();
    };
  }, [verifyConnection]);

  const login = async (email, password) => {
    // 10 second timeout prevents infinite "Verifying..." if network stalls
    const { data, error } = await withTimeout(
      supabase.auth.signInWithPassword({ email, password }),
      10000
    );
    if (error) throw error;
    
    // CRITICAL: Await user profile fetch before resolving.
    // This prevents the React Router race condition where it navigates to /portfolio 
    // before the user state is set, which would instantly kick the user back to /login.
    if (data?.user) {
      await fetchUserProfile(data.user);
    }
    return data.user;
  };

  const register = async (email, password, name) => {
    // 10 second timeout prevents infinite "Creating account..." if network stalls
    const { data, error } = await withTimeout(
      supabase.auth.signUp({
        email,
        password,
        options: { data: { name: name } }
      }),
      10000
    );
    if (error) throw error;
    
    // Ensure profile is loaded if auto-login succeeds
    if (data?.session) {
      await fetchUserProfile(data.user);
    }
    return { user: data.user, session: data.session };
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      sessionStorage.clear(); // Clear all session storage completely to ensure no stale data
    }
  };

  const resetPassword = async (email) => {
    const { error } = await withTimeout(
      supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/`,
      }),
      10000
    );
    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await withTimeout(
      supabase.auth.updateUser({ password: newPassword }),
      10000
    );
    if (error) throw error;
  };

  const value = {
    user,
    login,
    register,
    logout,
    resetPassword,
    updatePassword,
    loading,
    supabaseConnected
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};